import { useState, useEffect } from 'react'
import { 
  Bell, 
  Calendar, 
  Clock, 
  DollarSign, 
  AlertCircle, 
  X
} from 'lucide-react'
import { collection, getDocs, doc, setDoc, query } from 'firebase/firestore'
import { db, COLLECTIONS } from '../firebase/config'

const Notifications = () => {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [showPanel, setShowPanel] = useState(false)
  const [readNotifications, setReadNotifications] = useState(new Set())

  // Fetch notifications from various sources
  const fetchNotifications = async () => {
    setLoading(true)
    try {
      const allNotifications = []

      // 1. Recent/Pending Bookings (last 7 days, status: pending)
      try {
        const bookingsRef = collection(db, COLLECTIONS.BOOKINGS)
        const bookingsSnapshot = await getDocs(bookingsRef)
        
        bookingsSnapshot.docs.forEach(doc => {
          const data = doc.data()
          const status = data.status || 'pending'
          const createdAt = data.createdAt ? new Date(data.createdAt) : null
          
          // Only show pending bookings from last 7 days
          if (status === 'pending' && createdAt) {
            const daysDiff = (new Date() - createdAt) / (1000 * 60 * 60 * 24)
            if (daysDiff <= 7) {
              allNotifications.push({
                id: `booking-${doc.id}`,
                type: 'recent_booking',
                title: 'New Booking',
                message: `${data.name || 'Customer'} - ${data.category || ''} ${data.serviceType || ''}`,
                timestamp: createdAt,
                data: { bookingId: doc.id, booking: data },
                priority: 'high'
              })
            }
          }
        })
      } catch (error) {
        console.error('Error fetching pending bookings:', error)
      }

      // 2. Upcoming Bookings (today and tomorrow)
      try {
        const bookingsRef = collection(db, COLLECTIONS.BOOKINGS)
        const bookingsSnapshot = await getDocs(bookingsRef)
        
        const todayDate = new Date()
        todayDate.setHours(0, 0, 0, 0)
        const tomorrowDate = new Date(todayDate)
        tomorrowDate.setDate(tomorrowDate.getDate() + 1)
        
        bookingsSnapshot.docs.forEach(doc => {
          const data = doc.data()
          const status = data.status || 'pending'
          
          // Only show pending or confirmed bookings
          if ((status === 'pending' || status === 'confirmed') && data.date) {
            const bookingDate = new Date(data.date)
            bookingDate.setHours(0, 0, 0, 0)
            
            // Check if booking is today or tomorrow
            if (bookingDate.getTime() === todayDate.getTime() || 
                bookingDate.getTime() === tomorrowDate.getTime()) {
              
              const isToday = bookingDate.getTime() === todayDate.getTime()
              allNotifications.push({
                id: `upcoming-${doc.id}`,
                type: 'upcoming_booking',
                title: isToday ? 'Booking Today' : 'Booking Tomorrow',
                message: `${data.name || 'Customer'} at ${data.time || 'N/A'} - ${data.category || ''} ${data.serviceType || ''}`,
                timestamp: bookingDate,
                data: { bookingId: doc.id, booking: data, isToday },
                priority: 'high'
              })
            }
          }
        })
      } catch (error) {
        console.error('Error fetching upcoming bookings:', error)
      }

      // 3. Payment Alerts (outstanding balances)
      try {
        const bookingsRef = collection(db, COLLECTIONS.BOOKINGS)
        const bookingsSnapshot = await getDocs(bookingsRef)
        
        let totalOutstanding = 0
        let outstandingCount = 0
        const outstandingBookings = []
        
        bookingsSnapshot.docs.forEach(doc => {
          const data = doc.data()
          const paymentCompleted = data.paymentCompleted === true
          const balancePaid = data.balancePaid === true
          const balance = Number(data.balance) || 0
          
          // Check if booking has outstanding balance
          // Conditions: balance > 0 AND (payment not completed OR balance not paid)
          if (balance > 0 && (!paymentCompleted || !balancePaid)) {
            totalOutstanding += balance
            outstandingCount++
            outstandingBookings.push({
              id: doc.id,
              name: data.name || 'Unknown',
              balance: balance,
              date: data.date || 'N/A'
            })
          }
        })
        
        if (outstandingCount > 0) {
          allNotifications.push({
            id: 'payment-alert',
            type: 'payment_alert',
            title: 'Outstanding Payments',
            message: `${outstandingCount} booking${outstandingCount > 1 ? 's' : ''} with outstanding balance (GHS ${totalOutstanding.toFixed(2)})`,
            timestamp: new Date(),
            data: { 
              count: outstandingCount, 
              total: totalOutstanding,
              bookings: outstandingBookings
            },
            priority: 'medium'
          })
        }
      } catch (error) {
        console.error('Error fetching payment alerts:', error)
      }

      // 4. System Alerts (suspended gift cards)
      try {
        const giftCardsRef = collection(db, COLLECTIONS.GIFT_CARDS)
        const giftCardsSnapshot = await getDocs(giftCardsRef)
        
        let suspendedCount = 0
        
        giftCardsSnapshot.docs.forEach(doc => {
          const data = doc.data()
          if (data.suspended === true) {
            suspendedCount++
          }
        })
        
        if (suspendedCount > 0) {
          allNotifications.push({
            id: 'system-suspended-cards',
            type: 'system_alert',
            title: 'Suspended Gift Cards',
            message: `${suspendedCount} gift card${suspendedCount > 1 ? 's' : ''} are currently suspended`,
            timestamp: new Date(),
            data: { count: suspendedCount },
            priority: 'low'
          })
        }
      } catch (error) {
        console.error('Error fetching system alerts:', error)
      }

      // Sort by priority and timestamp
      allNotifications.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 }
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority]
        }
        return (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0)
      })

      setNotifications(allNotifications)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Load persisted read notifications from Firestore (collection: notificationReads)
    const loadReadsAndFetch = async () => {
      try {
        const readsRef = collection(db, 'notificationReads')
        const readsSnap = await getDocs(readsRef)
        const ids = readsSnap.docs.map(d => d.id)
        setReadNotifications(new Set(ids))
      } catch (err) {
        console.error('Failed to load persisted notifications from Firestore:', err)
      }

      // Fetch notifications after loading read states
      fetchNotifications()
    }

    loadReadsAndFetch()

    // Refresh notifications every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const unreadCount = notifications.filter(n => !readNotifications.has(n.id)).length

  const markAsRead = (notificationId) => {
    setReadNotifications(prev => {
      const next = new Set(prev)
      next.add(notificationId)
      return next
    })

    // Persist read to Firestore
    ;(async () => {
      try {
        const ref = doc(db, 'notificationReads', notificationId)
        await setDoc(ref, { read: true, readAt: new Date().toISOString() })
      } catch (err) {
        console.error('Failed to persist read notification to Firestore:', err)
      }
    })()
  }

  const markAllAsRead = () => {
    const ids = notifications.map(n => n.id)
    setReadNotifications(new Set(ids))

    // Persist all reads to Firestore
    ;(async () => {
      try {
        await Promise.all(ids.map(id => setDoc(doc(db, 'notificationReads', id), { read: true, readAt: new Date().toISOString() })))
      } catch (err) {
        console.error('Failed to persist read notifications to Firestore:', err)
      }
    })()
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'recent_booking':
        return <Calendar className="w-5 h-5 text-blue-500" />
      case 'upcoming_booking':
        return <Clock className="w-5 h-5 text-green-500" />
      case 'payment_alert':
        return <DollarSign className="w-5 h-5 text-yellow-500" />
      case 'system_alert':
        return <AlertCircle className="w-5 h-5 text-orange-500" />
      default:
        return <Bell className="w-5 h-5 text-gray-500" />
    }
  }

  const getNotificationColor = (type) => {
    switch (type) {
      case 'recent_booking':
        return 'bg-blue-500/10 border-blue-500/20'
      case 'upcoming_booking':
        return 'bg-green-500/10 border-green-500/20'
      case 'payment_alert':
        return 'bg-yellow-500/10 border-yellow-500/20'
      case 'system_alert':
        return 'bg-orange-500/10 border-orange-500/20'
      default:
        return 'bg-gray-500/10 border-gray-500/20'
    }
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now'
    const now = new Date()
    const diff = now - timestamp
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="relative p-2 text-gray-300 hover:text-white transition-colors duration-200"
      >
        <Bell className="w-5 h-5 lg:w-6 lg:h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowPanel(false)}
          />
          <div className="absolute right-0 mt-2 w-80 md:w-96 bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden z-50 max-h-[600px] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between bg-gray-900">
              <div className="flex items-center space-x-2">
                <Bell className="w-5 h-5 text-white" />
                <h3 className="text-lg font-semibold text-white">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-500 rounded-full text-xs text-white font-bold">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setShowPanel(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <p className="text-gray-400 mt-2 text-sm">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {notifications.map((notification) => {
                    const isRead = readNotifications.has(notification.id)
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-gray-700/50 transition-colors cursor-pointer ${
                          !isRead ? getNotificationColor(notification.type) : 'opacity-75'
                        }`}
                        onClick={() => markAsRead(notification.id)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`text-sm font-semibold ${isRead ? 'text-gray-400' : 'text-white'}`}>
                                {notification.title}
                              </p>
                              {!isRead && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                              )}
                            </div>
                            <p className="text-sm text-gray-300 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatTime(notification.timestamp)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-700 bg-gray-900">
              <button
                onClick={fetchNotifications}
                className="w-full text-sm text-gray-400 hover:text-white transition-colors text-center"
              >
                Refresh
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Notifications

